import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/hooks/use-toast';
import { requestThrottler } from '@/utils/requestThrottle';
import type { Database } from '@/integrations/supabase/types';

export interface Prospect {
  id: string;
  name: string;
  phone: string;
  email?: string;
  company?: string;
  notes?: string;
  status?: 'pending' | 'contacted' | 'successful' | 'failed';
  contactedAt?: Date;
  createdAt: Date;
  updatedAt: Date;
}

export type ProspectListRow = Database['public']['Tables']['prospect_lists']['Row'];
export type ProspectListInsert = Database['public']['Tables']['prospect_lists']['Insert'];

export interface ProspectList extends Omit<ProspectListRow, 'prospects'> {
  prospects: Prospect[];
}

// Global cache for prospect lists
let globalProspectListsCache: ProspectList[] = [];
let lastFetchTime = 0;
let currentUserId: string | null = null;
const CACHE_DURATION = 15 * 60 * 1000; // 15 minutes

export function useOptimizedSupabaseProspectLists() {
  const [prospectLists, setProspectLists] = useState<ProspectList[]>([]);
  const [loading, setLoading] = useState(true);
  const { user, session } = useAuth();
  const { toast } = useToast();
  const isMountedRef = useRef(true);

  const fetchProspectLists = async (forceRefresh = false) => {
    const userId = user?.id || session?.user?.id;
    if (!userId) {
      setProspectLists([]);
      setLoading(false);
      return;
    }

    const now = Date.now();
    
    // Use cache if valid and for same user
    if (!forceRefresh && 
        globalProspectListsCache.length > 0 && 
        currentUserId === userId && 
        (now - lastFetchTime) < CACHE_DURATION) {
      console.log('[ProspectLists] Using cached data');
      setProspectLists(globalProspectListsCache);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      
      // Use request throttler to prevent duplicate calls
      const cacheKey = `prospect-lists-${userId}`;
      
      const data = await requestThrottler.throttle(
        cacheKey,
        async () => {
          const { data, error } = await supabase
            .from('prospect_lists')
            .select('*')
            .eq('user_id', userId)
            .order('created_at', { ascending: false });

          if (error) throw error;
          return data || [];
        },
        { ttl: CACHE_DURATION }
      );

      const transformedLists: ProspectList[] = data.map(row => ({
        ...row,
        prospects: Array.isArray(row.prospects) ? row.prospects as unknown as Prospect[] : []
      }));

      // Update global cache
      globalProspectListsCache = transformedLists;
      lastFetchTime = now;
      currentUserId = userId;
      
      if (isMountedRef.current) {
        setProspectLists(transformedLists);
      }
    } catch (error) {
      console.error('Error fetching prospect lists:', error);
      if (isMountedRef.current) {
        toast({
          title: "Error",
          description: "Failed to fetch prospect lists",
          variant: "destructive",
        });
      }
    } finally {
      if (isMountedRef.current) {
        setLoading(false);
      }
    }
  };

  const addProspectList = async (listData: Omit<ProspectListInsert, 'id' | 'user_id' | 'created_at' | 'updated_at'> & { prospects?: Prospect[] }) => {
    try {
      const userId = user?.id || session?.user?.id;
      if (!userId) throw new Error('User not authenticated');

      // Prepare the insert payload with proper field mapping
      const insertPayload = {
        name: listData.name,
        description: listData.description,
        prospects: listData.prospects || [],
        tcpa_compliance: listData.tcpa_compliance || false,
        tcpa_acknowledged_at: listData.tcpa_acknowledged_at || null,
        tcpa_acknowledged_by: listData.tcpa_acknowledged_by || null,
        user_id: userId
      };

      const { data, error } = await supabase
        .from('prospect_lists')
        .insert([insertPayload])
        .select()
        .single();

      if (error) {
        console.error('Supabase insert error:', error);
        throw error;
      }

      // Clear cache and refresh
      requestThrottler.clearPattern('prospect-lists-');
      await fetchProspectLists(true);

      const transformedData = {
        ...data,
        prospects: Array.isArray(data.prospects) ? data.prospects as unknown as Prospect[] : []
      };

      toast({
        title: "Success",
        description: "Prospect list created successfully",
      });

      return transformedData;
    } catch (error) {
      console.error('Failed to create prospect list:', error);
      toast({
        title: "Error",
        description: error instanceof Error ? error.message : "Failed to create prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const updateProspectList = async (id: string, updates: Partial<ProspectList>) => {
    try {
      const updateData = {
        ...updates,
        prospects: updates.prospects ? updates.prospects as any : undefined
      };
      
      const { data, error } = await supabase
        .from('prospect_lists')
        .update(updateData)
        .eq('id', id)
        .select()
        .single();

      if (error) throw error;

      // Clear cache and refresh
      requestThrottler.clearPattern('prospect-lists-');
      await fetchProspectLists(true);

      const transformedData = {
        ...data,
        prospects: Array.isArray(data.prospects) ? data.prospects as unknown as Prospect[] : []
      };

      toast({
        title: "Success",
        description: "Prospect list updated successfully",
      });

      return transformedData;
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to update prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const deleteProspectList = async (id: string) => {
    try {
      const { error } = await supabase
        .from('prospect_lists')
        .delete()
        .eq('id', id);

      if (error) throw error;

      // Clear cache and refresh
      requestThrottler.clearPattern('prospect-lists-');
      await fetchProspectLists(true);

      toast({
        title: "Success",
        description: "Prospect list deleted successfully",
      });
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete prospect list",
        variant: "destructive",
      });
      throw error;
    }
  };

  const acknowledgeTCPA = async (listId: string, acknowledgedBy: string = 'User') => {
    return updateProspectList(listId, {
      tcpa_compliance: true,
      tcpa_acknowledged_at: new Date().toISOString(),
      tcpa_acknowledged_by: acknowledgedBy
    });
  };

  const getCompanies = () => {
    const allProspects = prospectLists.flatMap(list => list.prospects);
    return Array.from(new Set(allProspects.map(p => p.company).filter(Boolean)));
  };

  // Initialize data when user becomes available
  useEffect(() => {
    const userId = user?.id || session?.user?.id;
    if (userId) {
      fetchProspectLists();
    }
  }, [user?.id, session?.user?.id]);
  
  // Cleanup on unmount
  useEffect(() => {
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  return {
    prospectLists,
    loading,
    addProspectList,
    updateProspectList,
    deleteProspectList,
    acknowledgeTCPA,
    refreshProspectLists: () => fetchProspectLists(true),
    getCompanies,
  };
}

// Utility to clear global cache (useful for logout)
export const clearProspectListsCache = () => {
  globalProspectListsCache = [];
  lastFetchTime = 0;
  currentUserId = null;
  requestThrottler.clearPattern('prospect-lists-');
};